import type React from 'react';

import { useGetEvent } from '@vlabs/api/artemy/client';
import { getArtemySampleUrl } from '@vlabs/api/axios/point2-client';
import { DetectionIcon, ArrowInCircleRightIcon } from '@vlabs/icons';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { routes } from '@vlabs/shared/navigation/routes';
import { formatDateTime, DATE_FORMATS } from '@vlabs/shared/utils/date';
import { Tag } from '@vlabs/uikit';
import { Link } from '@vlabs/uikit/components/controls/link/Link';
import { generatePath } from 'react-router-dom';

import { StreamLinkPoint3 } from '../../stream-link/StreamLink.point3';
import { EventCardSample } from '../EventCardSample';
import { useEventCardTranslation } from '../i18n';
import st from './shared.module.sass';

interface EventMatchCardPoint3Props {
  eventId: string;
  matcherType?: 'face' | 'body';
  similarity?: number;
}

function EventMatchCardPoint3({
  eventId,
  matcherType = 'face',
  similarity = undefined,
}: EventMatchCardPoint3Props): React.JSX.Element | null {
  const { t } = useEventCardTranslation();
  const { data: event } = useGetEvent(eventId, { target: ['face_detections', 'body_detections', 'create_time', 'stream_id', 'handler_id'] });

  const faceSampleId = event?.face_detections?.find(({ sample_id }) => sample_id !== null)?.sample_id;
  const bodySampleId = event?.body_detections?.find(({ sample_id }) => sample_id !== null)?.sample_id;
  const sampleFaceSrc = getArtemySampleUrl('faces', faceSampleId);
  const sampleBodySrc = getArtemySampleUrl('bodies', bodySampleId);

  if (!event) return null;

  return (
    <div className={st.Card}>
      <div className={st.PrimarySection}>
        <EventCardSample
          matcherType={matcherType}
          sampleBodySrc={sampleBodySrc}
          sampleFaceSrc={sampleFaceSrc}
        />
        <div className={st.Details}>
          <Tag
            kind="neutral"
            size="large"
            value={t('match_tag')}
          />
          {similarity != null && (
            <SimilarityTag
              icon={<DetectionIcon />}
              kind="primary"
              size="large"
              title={t('similarity_event')}
              value={similarity}
            />
          )}
          <div className={st.Date}>{formatDateTime(event?.create_time, DATE_FORMATS.withYear)}</div>
        </div>
      </div>
      <div className={st.SecondarySection}>
        <div className={st.RowItem}>
          <div className={st.RowTitle}>{t('source')}</div>
          <StreamLinkPoint3
            kind="secondary"
            streamId={event.stream_id}
          />
        </div>
        <div className={st.RowItem}>
          <div className={st.RowTitle}>{t('handler')}</div>
          <TruncatedText
            hasTruncation
            tooltipContent={event.handler_id ?? ''}
          >
            {event.handler_id ?? '-'}
          </TruncatedText>
        </div>
      </div>
      <Link
        className={st.Link}
        icon={<ArrowInCircleRightIcon />}
        size="medium"
        target="_blank"
        // @ts-expect-error lack routes types
        to={generatePath(routes?.events?.eventDetails, { eventId })}
      >
        {t('event_link')}
      </Link>
    </div>
  );
}

export { EventMatchCardPoint3 };
